import React, { useCallback, useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "./ui/dialog";
import { Button } from "./ui/button";
import { api } from "../lib/api";
import { CopyCheck, Loader2, ChevronDown, ChevronUp } from "lucide-react";

const fmtDate = (d) => (d ? d.split("-").reverse().join("/") : "—");

export const DuplicadosDialog = ({ open, onOpenChange, tipoRegistro = "PROCESSO", ano }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [aberto, setAberto] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const { data: res } = await api.get("/processos/duplicados", {
        params: { tipo_registro: tipoRegistro, ano: ano ? Number(ano) : undefined },
      });
      setData(res);
    } finally {
      setLoading(false);
    }
  }, [tipoRegistro, ano]);

  useEffect(() => {
    if (open) load();
  }, [open, load]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl w-[96vw] max-h-[90vh] overflow-y-auto" data-testid="duplicados-dialog">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 font-display tracking-tight">
            <CopyCheck className="h-5 w-5 text-primary" /> Processos repetidos na planilha
          </DialogTitle>
          <DialogDescription className="text-sm">
            Mesmo nº de processo e ano lançados em mais de uma linha. Confira e ajuste os registros
            divergentes — nada é excluído automaticamente.
          </DialogDescription>
        </DialogHeader>

        {loading && (
          <div className="py-16 grid place-items-center">
            <Loader2 className="h-6 w-6 animate-spin text-primary" />
          </div>
        )}

        {!loading && data && (
          <>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mt-2">
              {[
                ["Números repetidos", data.total_grupos],
                ["Registros envolvidos", data.total_registros],
                ["Excedentes", data.total_registros - data.total_grupos],
              ].map(([l, v]) => (
                <div key={l} className="border border-border p-4">
                  <p className="text-[11px] uppercase tracking-wider text-slate-500">{l}</p>
                  <p className="text-2xl font-semibold font-display mt-1 mono">
                    {v.toLocaleString("pt-BR")}
                  </p>
                </div>
              ))}
            </div>

            {data.itens.length === 0 ? (
              <p className="py-12 text-center text-slate-400 text-sm" data-testid="duplicados-vazio">
                Nenhum processo repetido encontrado.
              </p>
            ) : (
              <div className="border border-border mt-4">
                <table className="w-full text-sm" data-testid="duplicados-table">
                  <thead className="bg-slate-50">
                    <tr className="text-left text-[11px] uppercase tracking-wider text-slate-500">
                      <th className="px-3 py-2">Nº Processo</th>
                      <th className="px-3 py-2">Ano</th>
                      <th className="px-3 py-2 text-right">Lançamentos</th>
                      <th className="px-3 py-2"></th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.itens.map((it) => {
                      const key = `${it.numero_processo}-${it.ano}`;
                      const expandido = aberto === key;
                      return (
                        <React.Fragment key={key}>
                          <tr
                            onClick={() => setAberto(expandido ? null : key)}
                            data-testid={`duplicado-row-${it.numero_processo}`}
                            className="border-t border-border hover:bg-slate-50 cursor-pointer transition-colors duration-150"
                          >
                            <td className="px-3 py-2 mono font-medium">{it.numero_processo}</td>
                            <td className="px-3 py-2 mono text-xs">{it.ano ?? "—"}</td>
                            <td className="px-3 py-2 text-right mono text-amber-700">{it.qtd}x</td>
                            <td className="px-3 py-2 text-right text-slate-400">
                              {expandido ? <ChevronUp className="h-4 w-4 inline" /> : <ChevronDown className="h-4 w-4 inline" />}
                            </td>
                          </tr>
                          {expandido && (
                            <tr className="border-t border-border bg-slate-50/70">
                              <td colSpan={4} className="px-3 py-3">
                                <table className="w-full text-xs" data-testid={`duplicado-detalhe-${it.numero_processo}`}>
                                  <thead>
                                    <tr className="text-left text-[10px] uppercase tracking-wider text-slate-500">
                                      <th className="px-2 py-1">Entrada</th>
                                      <th className="px-2 py-1">Requerente</th>
                                      <th className="px-2 py-1">Unid. destino</th>
                                      <th className="px-2 py-1">Assunto</th>
                                      <th className="px-2 py-1">Status</th>
                                      <th className="px-2 py-1">Conclusão</th>
                                    </tr>
                                  </thead>
                                  <tbody>
                                    {it.registros.map((r) => (
                                      <tr key={r._id} className="border-t border-border">
                                        <td className="px-2 py-1 mono whitespace-nowrap">{fmtDate(r.data_entrada)}</td>
                                        <td className="px-2 py-1 max-w-[180px] truncate">{r.nome_requerente || "—"}</td>
                                        <td className="px-2 py-1">{r.unidade_destino || "—"}</td>
                                        <td className="px-2 py-1 max-w-[280px] truncate">{r.assunto || "—"}</td>
                                        <td className="px-2 py-1">{r.status || "—"}</td>
                                        <td className="px-2 py-1 mono whitespace-nowrap">{fmtDate(r.data_conclusao)}</td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </td>
                            </tr>
                          )}
                        </React.Fragment>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            <div className="flex justify-end pt-2">
              <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="close-duplicados-btn">
                Fechar
              </Button>
            </div>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
};
